import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { movies } from "@/mocks/movies";
import { categories } from "@/mocks/categories";
import MovieCard from "@/components/feature/MovieCard";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import FilterPanel, { type FilterState } from "./components/FilterPanel";

const PAGE_SIZE = 8;

const sortOptions = [
  { value: "latest", label: "最新上映" },
  { value: "rating", label: "评分最高" },
  { value: "hot", label: "最受欢迎" },
];

export default function CategoryPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const genreParam = searchParams.get("genre") || "";

  const [filters, setFilters] = useState<FilterState>({ genre: genreParam, decade: "", region: "", rating: "" });
  const [sort, setSort] = useState("latest");
  const [page, setPage] = useState(1);
  const [filterOpen, setFilterOpen] = useState(false);

  useEffect(() => {
    setFilters((f) => ({ ...f, genre: genreParam }));
    setPage(1);
  }, [genreParam]);

  useDocumentMeta(
    `欧美三级电影分类${genreParam ? ` - ${genreParam}` : ""} - 欧美三级影视大全`,
    `欧美三级电影分类页提供${genreParam ? `${genreParam}类` : "全部"}欧美三级影片的便捷检索，支持按类型、年代、地区与评分区间筛选与排序，快速查找你感兴趣的欧美影视作品。`,
    `欧美三级电影分类,${genreParam || "全部影片"},欧美影视筛选,三级电影资源,影片分类`
  );

  const genreList = categories.filter((c) => c.slug !== "合集专题").map((c) => c.slug);

  const decades = useMemo(() => {
    const set = new Set(movies.map((m) => `${Math.floor(m.year / 10) * 10}年代`));
    return Array.from(set).sort().reverse();
  }, []);

  const regions = useMemo(() => {
    const set = new Set(movies.map((m) => m.region));
    return Array.from(set);
  }, []);

  const filtered = useMemo(() => {
    let list = movies.filter((m) => {
      if (filters.genre && !m.genres.includes(filters.genre)) return false;
      if (filters.decade && `${Math.floor(m.year / 10) * 10}年代` !== filters.decade) return false;
      if (filters.region && m.region !== filters.region) return false;
      if (filters.rating === "8.0分以上" && m.rating < 8) return false;
      if (filters.rating === "7.0-7.9分" && (m.rating < 7 || m.rating >= 8)) return false;
      if (filters.rating === "6.0-6.9分" && (m.rating < 6 || m.rating >= 7)) return false;
      return true;
    });

    list = [...list].sort((a, b) => {
      if (sort === "rating") return b.rating - a.rating;
      if (sort === "hot") return Number(b.featured) - Number(a.featured) || b.rating - a.rating;
      return b.year - a.year || b.rating - a.rating;
    });
    return list;
  }, [filters, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const paged = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const handleChange = (patch: Partial<FilterState>) => {
    setPage(1);
    if (patch.genre !== undefined) {
      const next = new URLSearchParams(searchParams);
      if (patch.genre) next.set("genre", patch.genre);
      else next.delete("genre");
      setSearchParams(next);
    } else {
      setFilters((f) => ({ ...f, ...patch }));
    }
  };

  const handleReset = () => {
    setFilters({ genre: "", decade: "", region: "", rating: "" });
    setSearchParams(new URLSearchParams());
    setPage(1);
  };

  const goPage = (p: number) => {
    setPage(p);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const activeGenreName = filters.genre ? `${filters.genre}` : "全部影片";

  return (
    <div className="mx-auto max-w-[1240px] px-4 py-6 md:px-6 md:py-8">
      <Breadcrumb items={[{ label: "电影分类", to: "/category" }, { label: activeGenreName }]} />

      <header className="mt-5">
        <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
          欧美三级电影分类 · {activeGenreName}
        </h1>
        <p className="mt-2 max-w-3xl text-sm leading-relaxed text-foreground-600">
          按类型、年代、地区与评分区间快速筛选欧美三级影视作品，支持按最新、评分与热度排序，帮助你高效找到想看的影片。
        </p>
      </header>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-[268px_1fr]">
        <aside className="hidden lg:block">
          <div className="sticky top-24 rounded-lg border border-background-200/80 bg-background-50 p-5">
            <FilterPanel
              state={filters}
              onChange={handleChange}
              onReset={handleReset}
              genres={genreList}
              decades={decades}
              regions={regions}
            />
          </div>
        </aside>

        <section>
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-background-200/80 bg-background-50 px-4 py-3">
            <p className="text-sm text-foreground-700">
              共找到 <span className="font-semibold text-primary-700">{filtered.length}</span> 部影片
            </p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setFilterOpen(true)}
                className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 px-3 py-1.5 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-700 lg:hidden"
              >
                <i className="ri-equalizer-line"></i>
                筛选
              </button>
              <div className="flex items-center gap-2 rounded-md border border-background-300 bg-background-50 px-2.5 py-1.5">
                <i className="ri-sort-desc text-base text-secondary-500"></i>
                <select
                  value={sort}
                  onChange={(e) => {
                    setSort(e.target.value);
                    setPage(1);
                  }}
                  className="cursor-pointer bg-transparent text-sm text-foreground-800 focus:outline-none"
                >
                  {sortOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {paged.length > 0 ? (
            <>
              <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 md:gap-4">
                {paged.map((movie) => (
                  <MovieCard key={movie.id} movie={movie} />
                ))}
              </div>

              {totalPages > 1 && (
                <nav aria-label="分页" className="mt-8 flex flex-wrap items-center justify-center gap-2">
                  <button
                    type="button"
                    disabled={currentPage === 1}
                    onClick={() => goPage(currentPage - 1)}
                    className="flex h-9 cursor-pointer items-center gap-1 rounded-md border border-background-300 px-3 text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    <i className="ri-arrow-left-s-line"></i>
                    上一页
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                    <button
                      key={p}
                      type="button"
                      onClick={() => goPage(p)}
                      className={[
                        "h-9 w-9 cursor-pointer rounded-md border text-sm font-medium transition-colors",
                        p === currentPage
                          ? "border-primary-600 bg-primary-600 text-background-50"
                          : "border-background-300 text-foreground-700 hover:border-primary-300 hover:text-primary-700",
                      ].join(" ")}
                    >
                      {p}
                    </button>
                  ))}
                  <button
                    type="button"
                    disabled={currentPage === totalPages}
                    onClick={() => goPage(currentPage + 1)}
                    className="flex h-9 cursor-pointer items-center gap-1 rounded-md border border-background-300 px-3 text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    下一页
                    <i className="ri-arrow-right-s-line"></i>
                  </button>
                </nav>
              )}
            </>
          ) : (
            <div className="mt-4 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-16 text-center">
              <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-3xl text-secondary-500">
                <i className="ri-file-search-line"></i>
              </span>
              <h3 className="mt-4 font-heading text-lg font-semibold text-foreground-900">没有匹配的影片</h3>
              <p className="mt-2 max-w-sm text-sm text-secondary-500">
                当前筛选条件下暂无结果，试试调整或重置筛选条件，发现更多精彩内容。
              </p>
              <button
                type="button"
                onClick={handleReset}
                className="mt-5 flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-600 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-700"
              >
                <i className="ri-refresh-line"></i>
                重置筛选条件
              </button>
            </div>
          )}
        </section>
      </div>

      {filterOpen && (
        <div className="fixed inset-0 z-[60] flex lg:hidden">
          <button
            type="button"
            aria-label="关闭筛选"
            onClick={() => setFilterOpen(false)}
            className="flex-1 cursor-pointer bg-primary-950/50 backdrop-blur-sm"
          ></button>
          <div className="animate-fade-in flex h-full w-[85%] max-w-sm flex-col overflow-y-auto bg-background-50 p-5">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="font-heading text-lg font-bold text-foreground-950">筛选影片</h2>
              <button
                type="button"
                aria-label="关闭"
                onClick={() => setFilterOpen(false)}
                className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md text-foreground-700 hover:bg-background-100"
              >
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>
            <FilterPanel
              state={filters}
              onChange={handleChange}
              onReset={handleReset}
              genres={genreList}
              decades={decades}
              regions={regions}
            />
            <button
              type="button"
              onClick={() => setFilterOpen(false)}
              className="mt-6 w-full cursor-pointer whitespace-nowrap rounded-md bg-primary-600 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-700"
            >
              查看 {filtered.length} 部影片
            </button>
          </div>
        </div>
      )}
    </div>
  );
}